import initSqlJs from 'sql.js';
import sqlWasmUrl from 'sql.js/dist/sql-wasm.wasm';
import { DEFAULT_USER } from './defaultUser';
import { getPersistence } from './persistence';

export const SCHEMA_SQL = `
  CREATE TABLE IF NOT EXISTS shifts (
    person TEXT NOT NULL,
    day_key TEXT NOT NULL,
    start_minutes INTEGER,
    end_minutes INTEGER,
    mark TEXT,
    PRIMARY KEY (person, day_key)
  );

  CREATE TABLE IF NOT EXISTS shift_changes (
    id TEXT PRIMARY KEY,
    position INTEGER NOT NULL,
    changed_at TEXT NOT NULL,
    person_a TEXT NOT NULL,
    day_a TEXT NOT NULL,
    range_a TEXT NOT NULL,
    person_b TEXT NOT NULL,
    day_b TEXT NOT NULL,
    range_b TEXT NOT NULL
  );

  CREATE TABLE IF NOT EXISTS users (
    id TEXT PRIMARY KEY,
    username TEXT NOT NULL UNIQUE COLLATE NOCASE,
    password_salt TEXT NOT NULL,
    password_hash TEXT NOT NULL,
    created_at TEXT NOT NULL
  );
`;

export type SqlDatabase = InstanceType<
  Awaited<ReturnType<typeof initSqlJs>>['Database']
>;

let dbPromise: Promise<SqlDatabase> | null = null;
let persistQueue: Promise<void> = Promise.resolve();

const initSql = async () =>
  initSqlJs({
    locateFile: (file) => (file === 'sql-wasm.wasm' ? sqlWasmUrl : file),
  });

export const persistDb = async (db: SqlDatabase) => {
  await getPersistence().write(db.export());
};

const hasUser = (db: SqlDatabase, username: string) => {
  const statement = db.prepare(
    'SELECT 1 AS present FROM users WHERE username = ? COLLATE NOCASE',
  );
  statement.bind([username]);
  const exists = statement.step();
  statement.free();
  return exists;
};

const ensureDefaultUser = (db: SqlDatabase) => {
  if (hasUser(db, DEFAULT_USER.username)) return false;

  db.run(
    `INSERT INTO users (id, username, password_salt, password_hash, created_at)
     VALUES (?, ?, ?, ?, ?)`,
    [
      DEFAULT_USER.id,
      DEFAULT_USER.username,
      DEFAULT_USER.passwordSalt,
      DEFAULT_USER.passwordHash,
      DEFAULT_USER.createdAt,
    ],
  );
  return true;
};

const openDb = async (): Promise<SqlDatabase> => {
  const SQL = await initSql();
  const persistence = getPersistence();
  const bytes = await persistence.read();
  const db = bytes ? new SQL.Database(bytes) : new SQL.Database();
  db.exec(SCHEMA_SQL);
  if (ensureDefaultUser(db)) {
    await persistDb(db);
  }
  return db;
};

export const getDb = () => {
  if (!dbPromise) {
    dbPromise = openDb().catch((error) => {
      dbPromise = null;
      throw error;
    });
  }

  return dbPromise;
};

export const withDbWrite = async <T>(
  fn: (db: SqlDatabase) => T | Promise<T>,
): Promise<T> => {
  const run = async () => {
    const db = await getDb();
    const result = await fn(db);
    await persistDb(db);
    return result;
  };

  const task = persistQueue.then(run, run);
  persistQueue = task.then(
    () => undefined,
    () => undefined,
  );
  return task;
};
